import { useGraphPropContext } from "../GraphPropsContext"
import { getDominant, translate } from "../../util/fmat_utils"
import { Circle, Group, Line, Text } from "react-konva"
import _ from "lodash"
import React from "react"
import { GraphNodeProps } from "../Types"
import { FMAT_GRID_PAD } from "./FMatQuadrantRenderer"

const positionPt = (v: number, n: number, l: number, pmin: number, pmax: number) => {
  const transform = translate[n]
  const cpad = 7
  const opad = 10
  const start = l / 2
  const width = l - cpad * 2 - opad * 2
  const norm = Math.max((((v - pmin) / (pmax - pmin)) * width) / 2, 0)
  return [
    Math.ceil(start + cpad * transform[0] + norm * transform[0]),
    Math.ceil(start + cpad * transform[1] + norm * transform[1]),
  ]
}

const adjustPad = (v: number, width: number) => {
  const half = width / 2
  return v > half ? v + FMAT_GRID_PAD : v
}

export const FMatQuadrantGraphNode: React.FC<GraphNodeProps> = ({
  value,
  hideLabel = false,
  strokeColor = "#000",
  opacity = 1.0,
  fill,
}) => {
  const { l, pmin, pmax } = useGraphPropContext()
  const width = l - FMAT_GRID_PAD
  if (value.length < 4) {
    console.error("Wrong value:", value)
    return null
  }
  const dots = [value[0], value[1], value[2], value[3]]
    .map((v, i) => [...positionPt(v, i, width, pmin, pmax), v])
    .map((v) => [adjustPad(v[0], width), adjustPad(v[1], width), v[2]])
  const fontSize = Math.ceil(width * 0.05)
  const fontOffsets = [
    [0, fontSize],
    [0, 0],
    [fontSize * 2, 0],
    [fontSize * 2, fontSize],
  ]

  return (
    <Group opacity={opacity}>
      <Line
        points={_(dots)
          .map((v) => [v[0], v[1]])
          .flatten()
          .value()}
        closed
        {...(fill ? { fill } : { stroke: strokeColor, strokeWidth: 3 })}
      />
      {!hideLabel &&
        dots.map((d, i) => (
          <React.Fragment key={i}>
            <Circle radius={fontSize / 4} x={d[0]} y={d[1]} fill={strokeColor} />
            <Text
              text={d[2] + ""}
              x={d[0]}
              y={d[1]}
              offsetX={fontOffsets[i][0]}
              offsetY={fontOffsets[i][1]}
              fill={strokeColor}
              fontSize={fontSize}
              fontStyle="bold"
              width={fontSize * 2}
              height={fontSize}
              align="center"
              verticalAlign="middle"
            />
          </React.Fragment>
        ))}
    </Group>
  )
}

export const FMatQuadrantTriangleNode = ({ value }) => {
  const { l, pmin, pmax } = useGraphPropContext()
  const width = l - FMAT_GRID_PAD
  const pad = FMAT_GRID_PAD / 2
  if (value.length < 6) {
    console.error("Wrong value=", value)
    return null
  }

  const [, , , , dw, hmi] = value
  const dominant = getDominant(value)
  const m = l / 2
  const x = m + (dominant <= 1 ? pad : -pad)
  const y = m + (dominant === 0 || dominant === 3 ? -pad : pad)
  const size = m - pad
  const xm = x + (dominant <= 1 ? size / 2 : -size / 2)
  const ym = y + (dominant === 0 || dominant === 3 ? -size / 2 : size / 2)
  const triangle =
    dominant >= 0 && hmi !== undefined
      ? [
          positionPt(value[dominant], dominant, width, pmin, pmax).map((v) => adjustPad(v, width)),
          [xm + Math.ceil(size * ((-1 * dw) / 22)), m],
          [m, ym + Math.ceil(size * ((-1 * hmi) / 120))],
        ]
      : null
  return <Line points={_.flatten(triangle)} fill="black" opacity={0.4} closed />
}
